import * as Sentry from '@sentry/react'
import { captureError, SENTRY_SEVERITY, toNavigationError } from 'src/shared/utils/sentry'

/**
 * 외부창 이동 모니터링
 * - 실제 이동은 기존과 동일 (window.open / location.replace / location.href)
 * - breadcrumb + 실패 시에만 Sentry
 * - 3초 URL 검증 skip: options.skipVerification
 *
 * = window.open(url, target, features) → monitoredOpen(url, context, target?, options?)
 * = window.location.replace(url)       → monitoredReplace(url, context, options?)
 * = window.location.href = url         → monitoredHrefReplace(url, context, options?)
 */

type MonitorOptions = {
  skipVerification?: boolean
  windowFeatures?: string
}

const VERIFY_TIMEOUT_MS = 3000

function shouldSkipVerification(options?: MonitorOptions): boolean {
  if (options?.skipVerification) return true
  return false
}

function createNavigationMeta(
  strategy: 'window-open' | 'location-replace' | 'location-href',
  context: string,
  url: string,
) {
  return {
    tags: { strategy, context },
    extra: {
      currentUrl: window.location.pathname,
      targetUrl: url,
    },
  }
}

/**
 * sentry 모니터링 window.open 랩핑함수
 *
 * 감지하는 실패 케이스:
 * 1. window.open 호출 자체가 예외를 던지는 경우
 * 2. 팝업이 생성되지 않았거나(null), 이미 닫혔거나, closed 속성이 없는 경우 (팝업 차단 등)
 * 3. 팝업은 열렸지만 3초 후에도 about:blank 상태인 경우 (target URL 이동 실패)
 *
 * @param url 새창에서 열 URL
 * @param context Sentry 태그/브레드크럼에 남길 호출 컨텍스트 식별자 (예: terms, footer)
 * @param target window.open의 target (기본값 '_blank')
 * @param options.skipVerification true면 3초 후 URL 이동 여부 검증을 건너뜀 (breadcrumb·팝업 null 캡처는 유지)
 * @param options.windowFeatures window.open 세 번째 인자 (예: width=400,height=600)
 * @returns 성공 시 Window 객체, 실패 시 null
 */
export function monitoredOpen(
  url: string,
  context: string,
  target = '_blank',
  options?: MonitorOptions,
) {
  let popup: Window | null = null
  const shouldSkip = shouldSkipVerification(options)
  const { tags, extra } = createNavigationMeta('window-open', context, url)

  Sentry.addBreadcrumb({
    category: 'navigation',
    message: `${context}[window.open]`,
    level: SENTRY_SEVERITY.Info,
    data: extra,
  })

  try {
    popup = window.open(url, target, options?.windowFeatures)
  } catch (e) {
    captureError(toNavigationError(new Error('[window.open] 새창 오픈 실패')), {
      level: SENTRY_SEVERITY.Warning,
      tags,
      fingerprint: ['window-open-error'],
      extra: {
        ...extra,
        error: e instanceof Error ? { name: e.name, message: e.message } : String(e),
      },
    })
    return null
  }

  // noopener는 스펙상 항상 null을 반환하므로 열림 여부를 검증할 수 없다 (breadcrumb·예외 캡처만 유지)
  if (/noopener/i.test(options?.windowFeatures ?? '')) return popup

  if (!popup || popup.closed || typeof popup.closed === 'undefined') {
    captureError(toNavigationError(new Error('[window.open] 새창 오픈 실패')), {
      level: SENTRY_SEVERITY.Warning,
      tags,
      fingerprint: ['window-open-error'],
      extra: { ...extra, popupClosed: popup?.closed },
    })
    return null
  }

  if (shouldSkip) return popup

  setTimeout(() => {
    try {
      if (popup!.closed) return
      const popupHref = popup!.location.href
      if (popupHref === 'about:blank' || popupHref === '') {
        captureError(toNavigationError(new Error('[window.open] URL 이동 실패')), {
          level: SENTRY_SEVERITY.Warning,
          tags,
          fingerprint: ['window-open-error'],
          extra: { ...extra, popupHref, popupClosed: popup!.closed },
        })
      }
    } catch {
      // SecurityError
    }
  }, VERIFY_TIMEOUT_MS)

  return popup
}

/**
 * sentry 모니터링 window.location.replace 랩핑함수
 *
 * 감지하는 실패 케이스:
 * 1. window.location.replace 호출 자체가 예외를 던지는 경우
 * 2. 호출 3초 후에도 URL이 이동 전(startHref) 그대로인 경우 (이동 미반영)
 *
 * @param url 이동할 URL
 * @param context Sentry 태그/브레드크럼에 남길 호출 컨텍스트 식별자
 * @param options.skipVerification true면 3초 후 이동 여부 검증을 건너뜀
 */
export function monitoredReplace(url: string, context: string, options?: MonitorOptions) {
  const shouldSkip = shouldSkipVerification(options)
  const startHref = window.location.href
  const { tags, extra } = createNavigationMeta('location-replace', context, url)

  Sentry.addBreadcrumb({
    category: 'navigation',
    message: `${context}[window.location.replace]`,
    level: SENTRY_SEVERITY.Info,
    data: extra,
  })

  try {
    window.location.replace(url)
  } catch (e) {
    captureError(toNavigationError(new Error('[window.location.replace] 이동 실패')), {
      level: SENTRY_SEVERITY.Warning,
      tags,
      fingerprint: ['window-location-replace-error'],
      extra: {
        ...extra,
        error: e instanceof Error ? { name: e.name, message: e.message } : String(e),
      },
    })
    return
  }

  if (shouldSkip) return

  setTimeout(() => {
    if (window.location.href === startHref) {
      captureError(toNavigationError(new Error('[window.location.replace] 이동 실패 (타임아웃)')), {
        level: SENTRY_SEVERITY.Warning,
        tags,
        fingerprint: ['window-location-replace-error'],
        extra: { ...extra, currentUrl: window.location.href },
      })
    }
  }, VERIFY_TIMEOUT_MS)
}

/**
 * sentry 모니터링 window.location.href 랩핑함수
 *
 * 감지하는 실패 케이스:
 * 1. window.location.href 대입 자체가 예외를 던지는 경우
 * 2. 호출 3초 후에도 URL이 이동 전(startHref) 그대로인 경우 (이동 미반영)
 *
 * @param url 이동할 URL
 * @param context Sentry 태그/브레드크럼에 남길 호출 컨텍스트 식별자
 * @param options.skipVerification true면 3초 후 이동 여부 검증을 건너뜀
 */
export function monitoredHrefReplace(url: string, context: string, options?: MonitorOptions) {
  const shouldSkip = shouldSkipVerification(options)
  const startHref = window.location.href
  const { tags, extra } = createNavigationMeta('location-href', context, url)

  Sentry.addBreadcrumb({
    category: 'navigation',
    message: `${context}[window.location.href]`,
    level: SENTRY_SEVERITY.Info,
    data: extra,
  })

  try {
    window.location.href = url
  } catch (e) {
    captureError(toNavigationError(new Error('[window.location.href] 이동 실패')), {
      level: SENTRY_SEVERITY.Warning,
      tags,
      fingerprint: ['window-location-href-error'],
      extra: {
        ...extra,
        error: e instanceof Error ? { name: e.name, message: e.message } : String(e),
      },
    })
    return
  }

  if (shouldSkip) return

  setTimeout(() => {
    if (window.location.href === startHref) {
      captureError(toNavigationError(new Error('[window.location.href] 이동 실패 (타임아웃)')), {
        level: SENTRY_SEVERITY.Warning,
        tags,
        fingerprint: ['window-location-href-error'],
        extra: { ...extra, currentUrl: window.location.href },
      })
    }
  }, VERIFY_TIMEOUT_MS)
}
